import { OrderLoyaltyReward } from '@eliadmin/core/src/components/loyalty/order-loyalty-reward';
import { ProfileRepeatOrderButton } from '@eliadmin/core/src/components/profile/profile-repeat-order-button';
import type { CustomerOrder } from '@eliadmin/core/src/api/customers/customers-types';
import { proformApiQuery } from '@eliadmin/core/src/api/proform/proform-api';
import { type ProformOrder } from '@eliadmin/core/src/api/proform/proform-types';
import { ProfileOrderDialog } from '@eliadmin/core/src/components/profile/profile-order-dialog';
import { ProfileOrderStatus } from '@eliadmin/core/src/components/profile/profile-order-status';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Card, CardContent } from '@eliadmin/core/src/components/ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@eliadmin/core/src/components/ui/tooltip';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { formatPrice, roundPrice } from '@eliadmin/core/src/utils/price-utils';
import { downloadBase64File } from '@eliadmin/core/src/utils/proform-utils';
import { useQuery } from '@tanstack/react-query';
import { FiSearch } from 'react-icons/fi';
import { LuDownload } from 'react-icons/lu';

export type ProfileOrderItemProps = {
  order: ProformOrder;
  eliOrder?: CustomerOrder;
};

export function ProfileOrderItem(props: ProfileOrderItemProps) {
  const currencySettings = useGlobalStore(s => s.generalLayout.currency_settings);
  const { t } = useTranslations();
  const customer = useCustomerStore(s => s.me!.customer);

  const orderDoklad = useQuery({
    queryKey: ['ELI_GET_ORDER', props.order.doklad],
    queryFn: () =>
      proformApiQuery('ELI_GET_ORDER', {
        e_mail: customer.email,
        doklad: props.order.doklad,
      }),
    enabled: false,
  });

  return (
    <Card className="rounded-none border-b first:border-t @xl:rounded-xl @xl:border">
      <CardContent className="hidden flex-row items-center gap-4 p-4 @xl:flex">
        <span className="flex-1 text-start text-sm font-bold">{props.order.doklad}</span>
        <span className="flex-1 text-start text-sm">{props.order.objednavka}</span>
        <span className="flex-1 text-start text-sm" suppressHydrationWarning>
          {new Date(props.order.datum_por).toLocaleDateString()}
        </span>
        <span className="flex-1 text-start text-sm font-bold">
          {formatPrice(roundPrice(props.order.cena_celkem), props.order.mena, currencySettings)}
        </span>
        {/* <div className="flex flex-1 flex-row justify-start">
          <ProfileOrderStatus order={props.order} />
        </div> */}
        <div className="flex flex-1 flex-row justify-end gap-2">
          {String(props.order.attach) === '1' && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size={'icon-lg'}
                    variant="ghost"
                    className="text-primary"
                    onClick={async () => {
                      const result = await orderDoklad.refetch();
                      if (result.data && result.data.root.order) {
                        const { file_name, att } = result.data.root.order;
                        downloadBase64File(att, file_name);
                      }
                    }}
                  >
                    <LuDownload className="h-6 w-6" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t('text1227')}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          <TooltipProvider>
            <Tooltip>
              <ProfileOrderDialog order={props.order} eliOrder={props.eliOrder}>
                <TooltipTrigger asChild>
                  <Button size={'icon-lg'} variant="ghost" className="text-primary">
                    <FiSearch className="h-6 w-6" />
                  </Button>
                </TooltipTrigger>
              </ProfileOrderDialog>
              <TooltipContent>
                <p>{t('text338')}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </CardContent>
      <CardContent className="flex flex-col gap-4 p-4 @xl:hidden ">
        <div className="flex flex-row items-center justify-between gap-2">
          <div className="flex flex-1 flex-col gap-1">
            <span className="flex-1 font-bold">{props.order.doklad}</span>
            <span className="flex-1 text-sm text-muted-foreground">{props.order.objednavka}</span>
            <span className="flex-1 text-sm text-muted-foreground">{props.order.datum_por}</span>
          </div>
          <div className="flex flex-row justify-end gap-2">
            {String(props.order.attach) === '1' && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size={'icon-lg'}
                      variant="ghost"
                      className="text-primary"
                      onClick={async () => {
                        const result = await orderDoklad.refetch();
                        if (result.data && result.data.root.order) {
                          const { file_name, att } = result.data.root.order;
                          downloadBase64File(att, file_name);
                        }
                      }}
                    >
                      <LuDownload className="h-6 w-6" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t('text1227')}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            <TooltipProvider>
              <Tooltip>
                <ProfileOrderDialog order={props.order} eliOrder={props.eliOrder}>
                  <TooltipTrigger asChild>
                    <Button size={'icon-lg'} variant="ghost" className="text-primary">
                      <FiSearch className="h-6 w-6" />
                    </Button>
                  </TooltipTrigger>
                </ProfileOrderDialog>
                <TooltipContent>
                  <p>{t('text338')}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
        <div className="flex flex-row items-center gap-2">
          <ProfileOrderStatus order={props.order} />
          <span className="flex-1 text-right font-bold">
            {formatPrice(roundPrice(props.order.cena_celkem), props.order.mena, currencySettings)}
          </span>
        </div>
      </CardContent>
      <ProfileRepeatOrderButton
        orderId={props.eliOrder?.id ?? 0}
        className="mx-4 mb-4 sm:ml-auto sm:max-w-sm"
      />

      <OrderLoyaltyReward
        reward={props.eliOrder?.loyalty_reward}
        compact
        className="w-full px-4 pb-4"
      />
    </Card>
  );
}
